import type { ObjectId } from 'mongodb';

import type { IsoDateTimeString } from './Helpers';
import { JobStatus } from './Jobs';
import { ProgramType } from './ProgramTypes';

export enum IncentiveType {
  renewableEnergyCredit = 'rec',
  // electrificationIncentive = 'electrificationIncentive',
  // gridServicesBESSIncentive = 'gridServicesBESSIncentive',
  // otherIncentive = 'otherIncentive',
  // upfrontBESSIncentive = 'upfrontBESSIncentive',
}

export const IncentiveTypeLabels: Record<IncentiveType, string> = {
  [IncentiveType.renewableEnergyCredit]: 'Renewable Energy Credit (REC)',
  // [IncentiveType.electrificationIncentive]: 'Electrification Incentive',
  // [IncentiveType.gridServicesBESSIncentive]: 'Grid Services BESS Incentive',
  // [IncentiveType.otherIncentive]: 'Other Incentive',
  // [IncentiveType.upfrontBESSIncentive]: 'Upfront BESS Incentive',
};

export enum RECIncentiveType {
  contracted = 'contracted',
  merchant = 'merchant',
  voluntary = 'voluntary',
}

export enum RECMonetizationMethod {
  forwardPrePayment = 'forwardPrepayment',
  upfrontPrepayment = 'upfrontPrepayment',
  spotMarket = 'spotMarket',
}

// This type will probably change as we model other types of incentives, but for now this is mainly just for RECs
export type AssetIncentiveAllocation = {
  id: ObjectId;
  incentiveKey: string;
  allocationDate: Date;
  lastEditedDate?: Date;
  monetizationMethod?: RECMonetizationMethod;
  name: string;
  partnerOrBuyer: string;
  recOwner: string;
  term: string;
  status: IncentiveAllocationStatus;
  /** Marker for the async account fan-out job that runs when the allocation is marked complete. */
  completionJob?: {
    jobId: string;
    status: JobStatus;
    startTime?: IsoDateTimeString;
    endTime?: IsoDateTimeString;
  };
};

export type CreateAllocationRequest = {
  name: string;
  allocationDate: string;
  monetizationMethod?: RECMonetizationMethod;
  recOwner?: string;
  partnerOrBuyer?: string;
  term: string;
  accountIds?: string[];
};

export type BlockAllocationRequest = {
  accountIds: string[];
  blockReason: string;
};

export type AssignAccountsRequest = {
  accountIds: string[];
  recType: RECIncentiveType;
};

export type UnallocateAccountsRequest = {
  accountIds: string[];
};

export type UpdateAllocationRequest = Partial<Omit<CreateAllocationRequest, 'accountIds'>> & {
  status?: IncentiveAllocationStatus;
};

export interface ActionOnAllocationAccountsResponse {
  actionedAccounts: string[];
  invalidAccounts: { accountId: string; reason: string }[];
  accountsAlreadyActioned: { accountId: string; allocationName: string }[];
}

export const AllocationTermOptions = [
  { value: 'full-term', label: 'Full-term' },
  { value: '3-year', label: '3 Year' },
  { value: '5-year', label: '5 Year' },
  { value: '10-year', label: '10 Year' },
  { value: 'spot', label: 'Spot' },
] as const;

export const RECMonetizationMethodLabels: Record<RECMonetizationMethod, string> = {
  [RECMonetizationMethod.forwardPrePayment]: 'Forward Prepayment',
  [RECMonetizationMethod.upfrontPrepayment]: 'Upfront Prepayment',
  [RECMonetizationMethod.spotMarket]: 'Spot Market',
};

export const RECIncentiveTypeLabels: Record<RECIncentiveType, string> = {
  [RECIncentiveType.contracted]: 'Contracted',
  [RECIncentiveType.merchant]: 'Merchant',
  [RECIncentiveType.voluntary]: 'Voluntary',
};

export interface AssetIncentiveBlock {
  id: ObjectId;
  key: string;
  type: IncentiveType;
  subtype?: IncentiveSubtype;
  name: string;
  description?: string;
  programTypes: ProgramType[];
  allocations?: AssetIncentiveAllocation[];
  allocationCount: number;
  status: IncentiveStatus;
  expirationDate?: Date;
  salesforceId?: string;
}

export enum IncentiveSubtype {}
/*
  illinoisShines = 'illinoisShines',
  nySun = 'nySun',
  stateRebate = 'stateRebate',
  utilityRebate = 'utilityRebate',
  other = 'other',
*/

export const IncentiveSubtypeLabels: Record<IncentiveSubtype, string> = {};
/*
  [IncentiveSubtype.illinoisShines]: 'Illinois Shines',
  [IncentiveSubtype.nySun]: 'NY Sun',
  [IncentiveSubtype.stateRebate]: 'State Rebate',
  [IncentiveSubtype.utilityRebate]: 'Utility Rebate',
  [IncentiveSubtype.other]: 'Other',
  */

export enum IncentiveStatus {
  active = 'active',
  inactive = 'inactive',
}

export const IncentiveStatusLabels: Record<IncentiveStatus, string> = {
  [IncentiveStatus.active]: 'Active',
  [IncentiveStatus.inactive]: 'Inactive',
};

export enum IncentiveAllocationStatus {
  pending = 'pending',
  completed = 'completed',
  cancelled = 'cancelled',
}

export const IncentiveAllocationStatusLabels: Record<IncentiveAllocationStatus, string> = {
  [IncentiveAllocationStatus.pending]: 'Pending',
  [IncentiveAllocationStatus.completed]: 'Completed',
  [IncentiveAllocationStatus.cancelled]: 'Cancelled',
};

// Fields shown in the incentive list table view
type IncentiveListFields = 'key' | 'name' | 'type' | 'programTypes' | 'status' | 'allocationCount';

// IncentiveListItem - API response for list view (only fields needed for table display)
export type IncentiveListItem = Pick<AssetIncentiveBlock, IncentiveListFields> & {
  id: string;
};

// All editable incentive fields (used for create/update requests)
type IncentiveEditableFields =
  | 'name'
  | 'type'
  | 'subtype'
  | 'description'
  | 'programTypes'
  | 'status'
  | 'expirationDate'
  | 'salesforceId';

export type IncentiveRequest = Pick<AssetIncentiveBlock, IncentiveEditableFields>;

// Create request - all editable fields (key is derived from name on backend)
export type CreateIncentiveRequest = IncentiveRequest;

// Update request - all fields optional for partial updates
export type UpdateIncentiveRequest = Partial<IncentiveRequest>;

// Full incentive detail (API response with string id for frontend)
export type Incentive = Omit<AssetIncentiveBlock, 'id'> & { _id: string };

export interface IncentiveSearchParams {
  searchTerm?: string;
  pageNum?: number;
  pageSize?: number;
  advancedFilters?: string;
}

export interface IncentiveSearchResponse {
  incentives: IncentiveListItem[];
  total: number;
  page: number;
  limit: number;
}

// Allocation account types (shared between server-core and frontend)

export interface AllocationAccountSummary {
  accountId: string;
  dateAssigned: Date;
}

export interface GetAccountsForAllocationResponse {
  accounts: AllocationAccountSummary[];
  total: number;
}

export interface IncentiveAccountAllocation {
  allocationId: string;
  allocationName: string;
  dateAssigned?: Date;
}

export interface IncentiveAccountSummary {
  _id: string;
  primaryApplicantName?: string;
  address?: {
    address1?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  applicants?: Array<{ email?: string }>;
  currentMilestone?: {
    name: string;
    status: string;
    type: string;
  };
  milestones?: Array<{
    name: string;
    status: string;
    type: string;
    order: number;
  }>;
  allocations: IncentiveAccountAllocation[];
  systemSizeKw?: number;
  totalSystemCost?: number;
}

export interface SearchIncentiveAccountsResponse {
  accounts: IncentiveAccountSummary[];
  totalCount: number;
  page: number;
  limit: number;
}

export interface SearchIncentiveAccountsInput {
  incentiveKey: string;
  searchTerm?: string;
  pageNum?: number;
  pageSize?: number;
  advancedFilters?: string;
}
